import React, { createContext, useContext, useState, useEffect } from "react";
import PropTypes from "prop-types";
import { getDcvStreamingMode } from "../api/DcvController";

const StreamingContext = createContext();

// Storage keys and helper functions
const STORAGE_KEYS = { 
    server: "streaming.server", 
    override: "streaming.override" 
};
const toStr = (b) => (b ? "dcv" : "vagon");
const toBool = (s) => s === "dcv";

export const StreamingProvider = ({ children }) => {
    const [isDcvStreaming, setIsDcvStreaming] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // Fetch streaming mode from API on component mount
    useEffect(() => {
        let mounted = true;

        const boot = async () => {
            setIsLoading(true);

            // Apply override immediately if it exists
            const overrideStr = localStorage.getItem(STORAGE_KEYS.override);
            if (overrideStr !== null) {
                setIsDcvStreaming(toBool(overrideStr));
            }

            try {
                // Fetch from API
                const response = await getDcvStreamingMode();
                if (!mounted) return;
                
                if (response && response.isDcvStreamSource !== undefined) {
                    const server = !!response.isDcvStreamSource;
                    // Store server default in localStorage
                    localStorage.setItem(STORAGE_KEYS.server, toStr(server));
                    
                    // Only use server value if no override exists
                    if (overrideStr === null) {
                        setIsDcvStreaming(server);
                    }
                }
            } catch (err) {
                console.error("Failed to fetch streaming mode:", err);
                setError(err);
                // Keep whatever is in state (override or initial default)
            } finally {
                if (mounted) setIsLoading(false);
            }
        };

        boot();

        // Cross-tab sync for "global reach"
        const onStorage = (e) => {
            if (e.key === STORAGE_KEYS.override) {
                const cur = e.newValue !== null
                    ? toBool(e.newValue)
                    : toBool(localStorage.getItem(STORAGE_KEYS.server));
                setIsDcvStreaming(cur);
            } else if (e.key === STORAGE_KEYS.server) {
                // If server value changes and no override exists, update state
                const overrideStr = localStorage.getItem(STORAGE_KEYS.override);
                if (overrideStr === null) {
                    setIsDcvStreaming(toBool(e.newValue));
                }
            }
        };
        window.addEventListener("storage", onStorage);
        
        return () => { 
            mounted = false; 
            window.removeEventListener("storage", onStorage); 
        };
    }, []);

    // Function to update streaming mode locally (shallow switch - no API call)
    const updateStreamingMode = (useDcv) => {
        localStorage.setItem(STORAGE_KEYS.override, toStr(useDcv));
        setIsDcvStreaming(useDcv);
    };

    // Function to clear override and revert to server default
    const clearStreamingOverride = () => {
        localStorage.removeItem(STORAGE_KEYS.override);
        const serverStr = localStorage.getItem(STORAGE_KEYS.server);
        if (serverStr !== null) {
            setIsDcvStreaming(toBool(serverStr));
        }
    };

    // Function to refresh streaming mode from API
    const refreshStreamingMode = async () => {
        try {
            setIsLoading(true);
            setError(null);
            const response = await getDcvStreamingMode();
            if (response && response.isDcvStreamSource !== undefined) {
                const server = !!response.isDcvStreamSource;
                localStorage.setItem(STORAGE_KEYS.server, toStr(server));
                
                // Only update if no override exists
                const overrideStr = localStorage.getItem(STORAGE_KEYS.override);
                if (overrideStr === null) {
                    setIsDcvStreaming(server);
                }
            }
        } catch (err) {
            console.error("Failed to refresh streaming mode:", err);
            setError(err);
        } finally {
            setIsLoading(false);
        }
    };

    const value = {
        isDcvStreaming,
        updateStreamingMode,
        clearStreamingOverride,
        refreshStreamingMode,
        isLoading,
        error
    };

    return (
        <StreamingContext.Provider value={value}>
            {children}
        </StreamingContext.Provider>
    );
};

export const useStreaming = () => {
    const context = useContext(StreamingContext);
    if (!context) {
        throw new Error("useStreaming must be used within a StreamingProvider");
    }
    return context;
};

StreamingProvider.propTypes = {
    children: PropTypes.node.isRequired
};
